import clsx from 'clsx'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Panel, SlpButton } from '../components/slp'
import '../styles/slp.css'
import { useSessionFiles } from './use-session-files'

/**
 * Session workspace (rt UI v2 spec §工作区 / F2) — every role gets it.
 *
 *   Script tab — studio uploads PDF/PNG/JPG (≤50MB) → R2; everyone views via
 *     the Chromium built-in viewer (iframe on the presigned url), with a file
 *     dropdown + "open in new window" for dual-screen actors.
 *   Takes tab — the existing upload queue + recovery card move in here
 *     (passed as `takesContent`, logic untouched; non-actor ends see a note).
 *
 * data-testid: rt-workspace / rt-workspace-tab-<name> / rt-script-viewer /
 * rt-file-upload (frozen in the spec).
 */

const TABS = [
  { id: 'script' as const, label: 'Script' },
  { id: 'takes' as const, label: 'Takes' },
]
type TabId = (typeof TABS)[number]['id']

const ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg'

export function WorkspacePanel({
  linkToken,
  sessionId,
  role,
  enabled,
  filesVersion,
  announceFiles,
  takesContent,
  className,
}: {
  linkToken: string
  sessionId: string
  role: string
  /** false in offline mock mode (no API) */
  enabled: boolean
  filesVersion: number
  announceFiles: () => void
  /** Takes tab body — the actor passes its existing queue + recovery card */
  takesContent?: ReactNode
  className?: string
}) {
  const sf = useSessionFiles({ linkToken, sessionId, enabled, filesVersion, announce: announceFiles })
  // Actors start on Takes (their queue + recovery card live there) and flip
  // to Script automatically the first time a file shows up — after a manual
  // tab click we never auto-switch again. Both tab bodies stay MOUNTED
  // (CSS-hidden) so the frozen e2e take/recovery selectors keep resolving.
  const [tab, setTabState] = useState<TabId>(takesContent ? 'takes' : 'script')
  const userPickedRef = useRef(false)
  const setTab = (t: TabId) => {
    userPickedRef.current = true
    setTabState(t)
  }
  const hasFiles = sf.files.length > 0
  useEffect(() => {
    if (hasFiles && !userPickedRef.current) setTabState('script')
  }, [hasFiles])
  const [activeId, setActiveId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const canUpload = role === 'studio'
  const active = sf.files.find((f) => f.id === activeId) ?? sf.files[0] ?? null

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // re-picking the same file must fire change again
    if (file) await sf.upload(file)
  }

  return (
    <Panel title="Workspace" className={className}>
      <div data-testid="rt-workspace">
        <div role="tablist" className="mb-2 flex gap-1 border-b border-[var(--slp-border)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`rt-workspace-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={clsx(
                'rounded-t px-3 py-1.5 text-[12px] font-semibold',
                tab === t.id
                  ? 'border border-b-0 border-[var(--slp-border)] bg-[var(--slp-panel)] text-[var(--slp-blue)]'
                  : 'text-[var(--slp-text-dim)] hover:text-[var(--slp-text)]',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* both bodies stay mounted; the inactive one is display:none */}
        <div className={clsx('flex-col gap-2', tab === 'script' ? 'flex' : 'hidden')}>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={active?.id ?? ''}
                onChange={(e) => setActiveId(e.target.value)}
                disabled={sf.files.length === 0}
                data-testid="rt-script-file"
                className={clsx(
                  'min-w-0 flex-1 rounded border border-[var(--slp-border)] bg-[var(--slp-panel)]',
                  'px-2 py-1 text-[13px] text-[var(--slp-text)]',
                  'focus:border-[var(--slp-blue)] focus:outline-none',
                  'disabled:bg-[var(--slp-panel-2)] disabled:text-[var(--slp-text-dim)]',
                )}
              >
                {sf.files.length === 0 ? (
                  <option value="">No files yet</option>
                ) : (
                  sf.files.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.filename}
                    </option>
                  ))
                )}
              </select>
              <SlpButton
                size="sm"
                variant="secondary"
                disabled={!active}
                onClick={() => active && window.open(active.url, '_blank', 'noopener')}
                data-testid="rt-script-popout"
                title="Open the script in its own window (dual-screen)"
              >
                Open in new window
              </SlpButton>
              {canUpload ? (
                <>
                  <SlpButton
                    size="sm"
                    disabled={sf.uploading || !enabled}
                    onClick={() => inputRef.current?.click()}
                  >
                    {sf.uploading ? 'Uploading…' : 'Upload file'}
                  </SlpButton>
                  <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    data-testid="rt-file-upload"
                    onChange={(e) => void onPick(e)}
                    className="hidden"
                  />
                </>
              ) : null}
            </div>

            {sf.error ? (
              <p data-testid="rt-file-error" className="text-[12px] font-medium text-[var(--slp-lamp-red)]">
                {sf.error}
              </p>
            ) : null}

            {active ? (
              <iframe
                key={active.id}
                src={active.url}
                title={active.filename}
                data-testid="rt-script-viewer"
                className="h-[460px] w-full rounded border border-[var(--slp-border)] bg-white"
              />
            ) : (
              <div className="flex h-[180px] items-center justify-center rounded border border-dashed border-[var(--slp-border)] bg-[var(--slp-panel-2)] px-4 text-center text-[12px] text-[var(--slp-text-dim)]">
                {!enabled
                  ? 'Workspace files are unavailable in the offline demo.'
                  : sf.loading
                    ? 'Loading session files…'
                    : canUpload
                      ? 'Upload the script (PDF, PNG or JPG, up to 50 MB) and every participant will see it here.'
                      : 'No script yet — it will appear here as soon as the studio uploads one.'}
              </div>
            )}
        </div>
        <div className={clsx('flex-col gap-2', tab === 'takes' ? 'flex' : 'hidden')}>
          {takesContent ?? (
            <p className="text-[12px] text-[var(--slp-text-dim)]">
              Takes record on the actor's computer and upload from their end — follow progress in
              the Connection panel.
            </p>
          )}
        </div>
      </div>
    </Panel>
  )
}
